import React, { useContext, useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { UserContext } from 'providers/userProvider';
import { routes } from 'router/routes';

import { itemOf, signIn } from '../api';
import { DEVICE_TOKEN_KEY, SIGN_IN_PATHS } from '../constants';
import { readFlow, updateFlow } from '../flow';
import { isEmail } from '../rules';
import { Actions, BigButton, Check, Field, Frame, LinkRow, Messages, TextLink, Title } from '../ui';
import { useLive, useOutcome } from '../useOutcome';

export const BAD_EMAIL = 'Enter your email address, e.g. name@example.com.';

/** Sign in: email, password, keep me signed in. */
export const SignInScreen = () => {
  const { user } = useContext(UserContext);
  // Whoever was signed in on arrival goes on to the portal; a sign-in made here goes to the welcome screen.
  const [arrivedSignedIn] = useState(user);
  const [email, setEmail] = useState(() => readFlow().email || '');
  const [password, setPassword] = useState('');
  const [shown, setShown] = useState(false);
  const [keep, setKeep] = useState(() => readFlow().keepSignedIn ?? true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(() => readFlow().notice || '');
  const [pausedUntil, setPausedUntil] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const live = useLive();
  const outcome = useOutcome();

  // A one-time message (e.g. "Your password is saved") is shown once.
  useEffect(() => {
    if (notice) {
      updateFlow({ notice: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sign-in opens again once the pause is over.
  useEffect(() => {
    if (pausedUntil == null) {
      return undefined;
    }
    const timer = setTimeout(() => setPausedUntil(null), Math.max(0, pausedUntil - Date.now()));
    return () => clearTimeout(timer);
  }, [pausedUntil]);

  if (arrivedSignedIn) {
    return <Redirect to={arrivedSignedIn.isTemporaryPasswordChanged ? '/' : routes.FIRST_LOG_IN} />;
  }

  const paused = pausedUntil != null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (paused || busy) {
      return;
    }
    setNotice('');
    if (!isEmail(email)) {
      setError(BAD_EMAIL);
      return;
    }
    if (!password) {
      setError('Enter your password.');
      return;
    }
    setError('');
    setBusy(true);
    const trimmed = email.trim();
    updateFlow({ email: trimmed, keepSignedIn: keep });
    let deviceToken: string | null = null;
    try {
      deviceToken = localStorage.getItem(DEVICE_TOKEN_KEY);
    } catch {
      // No remembered device.
    }
    const answer = await signIn({ email: trimmed, password, keepSignedIn: keep, deviceToken });
    const result = itemOf(answer);
    if (result?.outcome === 'paused') {
      const until = result.pausedUntil ? Date.parse(result.pausedUntil) : NaN;
      if (live.current) {
        setBusy(false);
        setError(result.message || 'Too many tries. Sign-in is paused for 15 minutes, or reset your password now.');
        setPausedUntil(Number.isNaN(until) ? Date.now() + 15 * 60_000 : until);
      }
      return;
    }
    const message = await outcome(answer, { keepSignedIn: keep, email: trimmed });
    if (live.current) {
      setBusy(false);
      if (message) {
        setError(message);
      }
    }
  };

  return (
    <Frame>
      <Title title="Sign in to StrengthX" sub="For coaches and team members." />
      <form onSubmit={submit} noValidate>
        <Field
          id="sx-email"
          label="Email"
          value={email}
          onChange={(value) => {
            setEmail(value);
            setError('');
          }}
          inputMode="email"
          autoComplete="username"
          placeholder="you@example.com"
        />
        <Field
          id="sx-password"
          label="Password"
          value={password}
          onChange={(value) => {
            setPassword(value);
            if (!paused) {
              setError('');
            }
          }}
          autoComplete="current-password"
          aside={{ label: 'Forgot password?', to: SIGN_IN_PATHS.FORGOT }}
          password={{ shown, onToggle: () => setShown((now) => !now) }}
        />
        <Check
          checked={keep}
          onChange={setKeep}
          label="Keep me signed in on this device"
          sub="For 30 days. Don’t use on shared computers."
        />
        <Messages error={error} ok={notice} />
        <Actions>
          <BigButton type="submit" disabled={paused} aria-busy={busy}>
            {paused ? 'Sign-in paused' : 'Sign in'}
          </BigButton>
        </Actions>
        {paused && (
          <LinkRow>
            <TextLink to={SIGN_IN_PATHS.FORGOT}>Reset my password</TextLink>
          </LinkRow>
        )}
      </form>
    </Frame>
  );
};
